import React, { useState, useEffect } from 'react';
import { 
  ChevronsLeft, 
  Sun, 
  Moon, 
  ZoomIn, 
  ZoomOut, 
  Type, 
  Contrast,
  MousePointer, 
  AlignCenter, 
  Underline,
  X,
  Volume2,
  VolumeX,
  Mic
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/components/theme-provider';

interface AccessibilityPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onVoiceAssistantToggle?: (isVisible: boolean) => void;
}

const SETTINGS_KEY = 'accesstopia_accessibility_settings';

const getSavedSettings = () => {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (!saved) return null;
    return JSON.parse(saved);
  } catch {
    return null;
  }
};

const saveSettings = (settings: any) => {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {}
};

const AccessibilityPanel = ({ isOpen, onClose, onVoiceAssistantToggle }: AccessibilityPanelProps) => {
  const { theme, setTheme } = useTheme();

  const [fontSize, setFontSize] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [isDarkMode, setIsDarkMode] = useState(theme === "dark");
  const [isHighlightLinks, setIsHighlightLinks] = useState(false);
  const [isLargePointer, setIsLargePointer] = useState(false);
  const [isTextAlign, setIsTextAlign] = useState(false);
  const [isTextUnderline, setIsTextUnderline] = useState(false);
  const [isVoiceAssistantVisible, setIsVoiceAssistantVisible] = useState(true);

  useEffect(() => {
    const saved = getSavedSettings();
    if (saved) {
      setFontSize(saved.fontSize ?? 100);
      setContrast(saved.contrast ?? 100);
      setIsHighlightLinks(saved.isHighlightLinks ?? false);
      setIsLargePointer(saved.isLargePointer ?? false);
      setIsTextAlign(saved.isTextAlign ?? false);
      setIsTextUnderline(saved.isTextUnderline ?? false);
      setIsVoiceAssistantVisible(saved.isVoiceAssistantVisible ?? true);

      if (typeof saved.isDarkMode === "boolean") {
        setIsDarkMode(saved.isDarkMode);
        setTheme(saved.isDarkMode ? "dark" : "light");
      } else {
        setIsDarkMode(theme === "dark");
      }
    } else {
      setIsDarkMode(theme === "dark");
    }
  }, [theme, setTheme]);

  useEffect(() => {
    document.documentElement.style.setProperty('--accessibility-font-scale', `${fontSize}%`);
    document.documentElement.style.fontSize = `${fontSize}%`;
    return () => {
      document.documentElement.style.fontSize = '';
    };
  }, [fontSize]);
  
  useEffect(() => {
    document.documentElement.style.setProperty('--accessibility-contrast', `${contrast}%`);
    const htmlElement = document.documentElement;
    if (htmlElement) {
      htmlElement.style.filter = `contrast(${contrast}%)`;
    }
    return () => {
      htmlElement.style.filter = '';
    };
  }, [contrast]);

  useEffect(() => {
    if (isHighlightLinks) {
      document.querySelectorAll('a').forEach(link => {
        link.classList.add('accessibility-highlight-link');
      });
    } else {
      document.querySelectorAll('a').forEach(link => {
        link.classList.remove('accessibility-highlight-link');
      });
    }
  }, [isHighlightLinks]);
  
  useEffect(() => {
    if (isLargePointer) {
      const cursorStyle = document.createElement('style');
      cursorStyle.id = 'accessibility-cursor-style';
      cursorStyle.innerHTML = `
        .accessibility-large-pointer {
          cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m3 3 7.07 16.97 2.51-7.39 7.39-2.51L3 3z'/%3E%3Cpath d='m13 13 6 6'/%3E%3C/svg%3E") 0 0, auto !important;
        }
        .dark .accessibility-large-pointer {
          cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m3 3 7.07 16.97 2.51-7.39 7.39-2.51L3 3z'/%3E%3Cpath d='m13 13 6 6'/%3E%3C/svg%3E") 0 0, auto !important;
        }
        .accessibility-large-pointer .bg-white, 
        .accessibility-large-pointer [class*='bg-slate-1'], 
        .accessibility-large-pointer [class*='bg-gray-1'],
        .accessibility-large-pointer [class*='bg-zinc-1'] {
          cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m3 3 7.07 16.97 2.51-7.39 7.39-2.51L3 3z'/%3E%3Cpath d='m13 13 6 6'/%3E%3C/svg%3E") 0 0, auto !important;
        }
        .accessibility-large-pointer .bg-black,
        .accessibility-large-pointer [class*='bg-slate-9'],
        .accessibility-large-pointer [class*='bg-gray-9'],
        .accessibility-large-pointer [class*='bg-zinc-9'] {
          cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m3 3 7.07 16.97 2.51-7.39 7.39-2.51L3 3z'/%3E%3Cpath d='m13 13 6 6'/%3E%3C/svg%3E") 0 0, auto !important;
        }
      `;
      document.head.appendChild(cursorStyle);
      document.body.classList.add('accessibility-large-pointer');
    } else {
      document.body.classList.remove('accessibility-large-pointer');
      const cursorStyle = document.getElementById('accessibility-cursor-style');
      if (cursorStyle) {
        cursorStyle.remove();
      }
    }
  }, [isLargePointer]);
  
  useEffect(() => {
    if (isTextAlign) {
      const elements = document.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, span, div:not(.accesstopia-panel):not(.accesstopia-panel *)');
      elements.forEach(el => {
        (el as HTMLElement).style.textAlign = 'center';
      });
      document.body.classList.add('accessibility-text-center');
    } else {
      const elements = document.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, span, div:not(.accesstopia-panel):not(.accesstopia-panel *)');
      elements.forEach(el => {
        (el as HTMLElement).style.textAlign = '';
      });
      document.body.classList.remove('accessibility-text-center');
    }
  }, [isTextAlign]);
  
  useEffect(() => {
    if (isTextUnderline) {
      document.querySelectorAll('p, h1, h2, h3, h4, h5, h6, span, a').forEach(el => {
        el.classList.add('accessibility-underline');
      });
    } else {
      document.querySelectorAll('p, h1, h2, h3, h4, h5, h6, span, a').forEach(el => {
        el.classList.remove('accessibility-underline');
      });
    }
  }, [isTextUnderline]);
  
  useEffect(() => {
    saveSettings({
      fontSize,
      contrast,
      isDarkMode,
      isHighlightLinks,
      isLargePointer,
      isTextAlign,
      isTextUnderline,
      isVoiceAssistantVisible,
    });
  }, [fontSize, contrast, isDarkMode, isHighlightLinks, isLargePointer, isTextAlign, isTextUnderline, isVoiceAssistantVisible]);

  const handleReset = () => {
    setFontSize(100);
    setContrast(100);
    setIsDarkMode(false);
    setIsHighlightLinks(false);
    setIsLargePointer(false);
    setIsTextAlign(false);
    setIsTextUnderline(false);
    setIsVoiceAssistantVisible(true);
    setTheme("light");
    if (onVoiceAssistantToggle) onVoiceAssistantToggle(true);
  };

  const handleVoiceAssistantToggle = () => {
    const newValue = !isVoiceAssistantVisible;
    setIsVoiceAssistantVisible(newValue);
    onVoiceAssistantToggle?.(newValue);
  };

  function announceAccessibilityChange(msg: string) {
    import("sonner").then(({ toast }) => toast(msg));
    const liveRegion = document.getElementById('accessibility-live-region');
    if (liveRegion) {
      liveRegion.textContent = '';
      setTimeout(() => {
        liveRegion.textContent = msg;
      }, 40);
    }
  }

  const handleDarkModeChange = (checked: boolean) => {
    setIsDarkMode(checked);
    setTheme(checked ? "dark" : "light");
    const settings = getSavedSettings() || {};
    settings.isDarkMode = checked;
    saveSettings(settings);
    announceAccessibilityChange(checked ? 'Dark mode enabled' : 'Dark mode disabled');
  };

  const handleHighlightLinksChange = (checked: boolean) => {
    setIsHighlightLinks(checked);
    announceAccessibilityChange(checked ? 'Link highlighting enabled' : 'Link highlighting disabled');
  };

  const handleLargePointerChange = (checked: boolean) => {
    setIsLargePointer(checked);
    announceAccessibilityChange(checked ? 'Large pointer enabled' : 'Large pointer disabled');
  };

  const handleTextAlignChange = (checked: boolean) => {
    setIsTextAlign(checked);
    announceAccessibilityChange(checked ? 'Centered text enabled' : 'Centered text disabled');
  };

  const handleTextUnderlineChange = (checked: boolean) => {
    setIsTextUnderline(checked);
    announceAccessibilityChange(checked ? 'Underline enabled' : 'Underline disabled');
  };

  const handleFontSizeChange = (val: number[]) => {
    setFontSize(val[0]);
    announceAccessibilityChange("Font size set to " + val[0] + "%");
  };

  return (
    <div 
      className={cn(
        "fixed left-0 bottom-24 md:bottom-20 w-72 md:w-80 rounded-r-lg overflow-hidden z-50 ease-transition",
        "accessibility-shadow accessibility-glass",
        isOpen ? "animate-slide-in-left" : "animate-slide-out-left -translate-x-full"
      )}
      aria-hidden={!isOpen}
    >
      <div id="accessibility-live-region" aria-live="polite" className="sr-only" />
      <div className="p-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">Accessibility</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close accessibility panel"
            className="h-8 w-8 text-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Type className="h-4 w-4 text-foreground" />
                <span className="text-sm font-medium">Font Size</span>
              </div>
              <span className="text-xs">{fontSize}%</span>
            </div>
            <div className="flex items-center gap-2">
              <ZoomOut className="h-3 w-3 text-foreground" />
              <Slider
                value={[fontSize]}
                min={80}
                max={200}
                step={5}
                onValueChange={handleFontSizeChange}
                aria-label="Adjust font size"
              />
              <ZoomIn className="h-3 w-3 text-foreground" />
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Contrast className="h-4 w-4 text-foreground" />
                <span className="text-sm font-medium">Contrast</span>
              </div>
              <span className="text-xs">{contrast}%</span>
            </div>
            <Slider
              value={[contrast]}
              min={75}
              max={150}
              step={5}
              onValueChange={(value) => setContrast(value[0])}
              aria-label="Adjust contrast"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isDarkMode ? 
                <Moon className="h-4 w-4 text-foreground" /> : 
                <Sun className="h-4 w-4 text-foreground" />
              }
              <span className="text-sm font-medium">Dark Mode</span>
            </div>
            <Switch
              checked={isDarkMode}
              onCheckedChange={handleDarkModeChange}
              aria-label="Toggle dark mode"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Underline className="h-4 w-4 text-foreground" />
              <span className="text-sm font-medium">Highlight Links</span>
            </div>
            <Switch
              checked={isHighlightLinks}
              onCheckedChange={handleHighlightLinksChange}
              aria-label="Toggle link highlighting"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MousePointer className="h-4 w-4 text-foreground" />
              <span className="text-sm font-medium">Large Pointer</span>
            </div>
            <Switch
              checked={isLargePointer}
              onCheckedChange={handleLargePointerChange}
              aria-label="Toggle large pointer"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlignCenter className="h-4 w-4 text-foreground" />
              <span className="text-sm font-medium">Center Text</span>
            </div>
            <Switch
              checked={isTextAlign}
              onCheckedChange={handleTextAlignChange}
              aria-label="Toggle center text alignment"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Underline className="h-4 w-4 text-foreground" />
              <span className="text-sm font-medium">Underline Text</span>
            </div>
            <Switch
              checked={isTextUnderline}
              onCheckedChange={handleTextUnderlineChange}
              aria-label="Toggle text underlining"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isVoiceAssistantVisible ? (
                <Volume2 className="h-4 w-4 text-foreground" />
              ) : (
                <VolumeX className="h-4 w-4 text-foreground" />
              )}
              <span className="text-sm font-medium">Voice Assistant</span>
            </div>
            <Switch
              checked={isVoiceAssistantVisible}
              onCheckedChange={handleVoiceAssistantToggle}
              aria-label="Toggle voice assistant visibility"
            />
          </div>
          
          <Button 
            onClick={handleReset}
            variant="outline"
            className="w-full text-foreground"
          >
            Reset All Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityPanel;
